import { Measure, TranslatedEmb, Vision, VisionFilterKey, VisionKey, VisionStep, VisionStepPerimeter } from '@wizeoo/akm-message-lib';
import { BaselineEngine, INJECTION } from '../../manager/workingdata/baseline.engine';
import { IVisionKeyProperties } from '../../model/interfaces';
import { IVo, entity, field } from '../entity-maps';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizConstants } from '../../model/constants';

/**
 * VO représentant une vision
 */

@entity('wizeoo.allkeyme.vision.message.Vision', 'VISION')
export class VisionVO implements IVo  {

  constructor(pSource?: Vision) {   
      if (pSource) {
          this.source = pSource;
      } else {
          this.source = new Vision();
          this.source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
      }
  }

  /** L'objet source provenant du back */
  public source: Vision;

  @field('boolean')
  public get useScenario(): boolean {
    return this.source.useScenario;
  }
  public set useScenario(value: boolean) {
    this.source.useScenario = value;
  }

  @field('boolean')
  public get usePdfExport(): boolean {
    return this.source.usePdfExport;
  }
  public set usePdfExport(value: boolean) {
    this.source.usePdfExport = value;
  }

  @field('boolean')
  public get autoCompute(): boolean {
    return this.source.autoCompute;
  }
  public set autoCompute(value: boolean) {
    this.source.autoCompute = value;
  }

  @field('boolean')
  public get useCompute(): boolean {
    return this.source.useCompute;
  }
  public set useCompute(value: boolean) {
    this.source.useCompute = value;
  }

  // cron compute
  public get computeCron(): string {
    return this.source.computeCron;
  }
  public set computeCron(value: string) {
    this.source.computeCron = value;
  }

  // Id technique
  public get id(): number {
    return this.source.id;
  }
  public set id(value: number) {
    this.source.id = value;
  }

  // UID technique
  public get uid(): string {
    return this.source.uid;
  }
  public set uid(value: string) {
    this.source.uid = value;
  }

  // code domaine (root), technique
  public get domain(): string {
    return this.source.domain;
  }
  public set domain(value: string) {
    this.source.domain = value;
  }

  public get businessUnitCode(): string {
    return this.source.businessUnitCode;
  }
  public set businessUnitCode(value: string) {
    this.source.businessUnitCode = value;
  }

  @field('string')
  public get code(): string {
    return this.source.code;
  }
  public set code(pValue: string) {
    this.source.code = pValue;
  }

  @field('string')
  public get chronoCode(): string {
    // chrono associé
    return this.source.chronoCode;
  }
  public set chronoCode(value: string) {
    this.source.chronoCode = value;
  }

  // TODO : détecter les changements sur les sous-collections
  @field('string') // patch pour s'assurer de la demande de mise à jour sur contenu de steps
  public get maintenanceFlag(): string {
    return this.source.maintenanceFlag;
  }
  public set maintenanceFlag(value: string) {
    this.source.maintenanceFlag = value;
  }

  // collection de mesures
  @field('Measure', true)
  public get measures(): Measure[] {
      return this.source.measures;
  }
  public set measures(measures: Measure[]) {
      this.source.measures = measures;
  }

  @field('VisionStep', false, true)
  // attention, la clé, c'est l'ordre, pas le code !! (java cast les clés de Map en string)
  public get visionSteps(): {[key: number]: VisionStep} {
      return this.source.visionSteps;
  }
  public set visionSteps(visionSteps: {[key: number]: VisionStep}) {
      this.source.visionSteps = visionSteps;
  }

  @field('VisionKey', false, true)
  // attention, la clé, c'est l'ordre, pas le code !! (java cast les clés de Map en string)
  public get visionKeys(): {[key: number]: VisionKey} {
      return this.source.visionKeys;
  }
  public set visionKeys(visionKeys: {[key: number]: VisionKey}) {
    this.source.visionKeys = visionKeys;
  }

  @field('VisionFilterKey', false, true)
  // attention, la clé, c'est l'ordre, pas le code !! (java cast les clés de Map en string)
  public get visionFilterKeys(): {[key: number]: VisionFilterKey} {
    return this.source.visionFilterKeys;
  }
  public set visionFilterKeys(visionFilterKeys: {[key: number]: VisionFilterKey} ) {
    this.source.visionFilterKeys = visionFilterKeys;
  }

  // collection de stepPerimeters
  @field('visionStepPerimeters', false, true)
  public get visionStepPerimeters(): {[key: string]: VisionStepPerimeter} {
    return this.source.visionStepPerimeters;
  }
  public set visionStepPerimeters(visionStepPerimeters: {[key: string]: VisionStepPerimeter}) {
    this.source.visionStepPerimeters = visionStepPerimeters;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this.source.label = value;
  }
  public get label(): TranslatedEmb {
    return this.source.label;
  }

  @field('TranslatedEmb')
  public set description(value: TranslatedEmb) {
    this.source.description = value;
  }
  public get description(): TranslatedEmb {
    return this.source.description;
  }

  @field('string')
  public get status(): string {
    // statut de la vision
    return this.source.status;
  }
  public set status(value: string) {
    this.source.status = value;
  }

  @field('string')
  public get icon(): string {
    return this.source.icon;
  }

  public set icon(pValue: string) {
    this.source.icon = pValue;
  }

  @field('string')
  public get supportMail(): string {
    return this.source.supportMail;
  }
  public set supportMail(value: string) {
    this.source.supportMail = value;
  }

  @field('string')
  public get helpURL(): string {
    return this.source.helpURL;
  }
  public set helpURL(value: string) {
    this.source.helpURL = value;
  }

  @field('string')
  public get runMode(): string {
    return this.source.runMode;
  }
  public set runMode(value: string) {
    this.source.runMode = value;
  }

  @field('string')
  public get color(): string {
    return this.source.color;
  }
  public set color(value: string) {
    this.source.color = value;
  }

  // technique pour forcer le refresh
  public get minRevNumber(): number { 
    return this.source.minRevNumber;
  }
  public set minRevNumber(value: number) {
    this.source.minRevNumber = value;
  }

  /* ################################################
      données volatiles pour manipulations rapides
  ################################################ */

  public reset(): void {
    this._keySizeByLevel = null;
    this._stepKeyByLevel = null;
  }

  private _keySizeByLevel: Map<number, number> = null;
  // Nbre de visionKeys à prendre pour chaque step
  public get keySizeByLevel(): Map<number, number> {
    if(! this._keySizeByLevel) {
      this._keySizeByLevel = new Map();
      [...this.stepKeyByLevel.keys()].forEach(level => {
        this._keySizeByLevel.set(level, this.stepKeyByLevel.get(level).length);
      });
    }
    return this._keySizeByLevel;
  } 

  private _stepKeyByLevel: Map<number, VisionKey[]> = null;
  // l'ensemble des visionkeys pour ce step (avec héritage des steps précédents), dans l'ordre
  public get stepKeyByLevel(): Map<number,VisionKey[]> {;
    if(!this._stepKeyByLevel) { // création si non présente
      this._stepKeyByLevel = new Map();
      Object.values(this.visionSteps || {}).forEach((vs: VisionStep) => {
        this._stepKeyByLevel.set(vs.level, []);
        Object.values(this.visionKeys || {}).sort((a, b) => a.order - b.order).forEach((vk: VisionKey) => {
          if(vk.stepLevel <= vs.level) {
            this._stepKeyByLevel.get(vs.level).push(vk);
          }
        });
      });
    }
    return this._stepKeyByLevel;
  }



  public nbSteps: number = null; // nbr de steps pour calcul rapide
  
  public measureByLevel: Map<number,Map<string, Measure>> = null; // les mesures disponibles pour chaque niveau
  
  public keyProperties: Map<string, IVisionKeyProperties> = null; // les propriétés complémentaires pour les key & filterKey

  /* ################################################
      données de rendu (IHM)
  ################################################ */
  
  public loaded: boolean = false;
  
  public visionProperties: Map<string, any> = new Map(); // les propriétés complémentaires sur la vision pour le rendu IHM

  public get uniqueKey(): string {
    return [this.domain, this.businessUnitCode, this.code].join(WizConstants.KEY_SEPARATOR);
  }


  /**
   * libellé
   */
  public get trsLabel(): string {
    return BaselineEngine.injectorCall(INJECTION.VISION_LABEL, this) || null;
  }

  /**
   * description
   */
  public get trsDescription(): string {
      return BaselineEngine.injectorCall(INJECTION.VISION_DESC, this) || null;
  }

  /**
   * description
   */
  public get iconOrDie(): any {
    return BaselineEngine.injectorCall(INJECTION.VISION_ICON, this) || null;
  }

}
